/* Responsibility: Creates a slider element for filtering */
import dom from '../lib/dom.js';
import preconditions from '../lib/preconditions.js';
import createSlider from '../lib/slider.js';

const checkPreconditions = preconditions('sliderFilter', {
    root: 'element',
    fromInput: 'input',
    toInput: 'input',
    resultViewCriteria: 'object',
    eventHub: 'eventHub',
    resultViewName: 'string',
    formatter: 'object',
    urlKey: 'string',
    urlQueryParams: 'object',
});

export default function paleSliderFilter(params) {
    checkPreconditions(params);

    const resultViewName = params.resultViewName;

    let hasMaxLimit;

    const fromInput = params.fromInput;
    const toInput = params.toInput;

    const resultViewCriteria = params.resultViewCriteria;
    const formatter = params.formatter;
    const eventHub = params.eventHub;
    let slider;

    function updateSliderValues(values) {
        if (typeof values !== 'undefined' && values.length) {
            const split = values[0].split('-');
            slider.setStartValue(+formatter.calculateSliderValue(split[0]));
            slider.setEndValue(+formatter.calculateSliderValue(split[1]));
        } else {
            slider.setStartValue(slider._minValue);
            slider.setEndValue(slider._maxValue);
        }
    }

    function notInInitialPosition() {
        return Number(fromInput.value) !== Number(slider._minValue) || Number(toInput.value) !== Number(slider._maxValue);
    }

    function updateResultViewCriteria() {
        if (notInInitialPosition()) {
            resultViewCriteria[params.urlKey] = `${formatter.formatFilterValue(fromInput.value)}-${formatter.formatFilterValue(
                toInput.value,
            )}`;
        } else {
            delete resultViewCriteria[params.urlKey];
        }
    }

    function notifyFilterAdded() {
        if (notInInitialPosition()) {
            const start = formatter.formatStartLabel(fromInput.value);
            const endPrepend = Number(toInput.value) === Number(slider._maxValue) && hasMaxLimit ? 'over ' : '';
            const end = endPrepend + formatter.formatEndLabel(toInput.value);
            eventHub.publish('filters:added', {
                filterId: resultViewName,
                text: `${params.filterTitle + start} - ${end}`,
                urlValue: `${formatter.formatFilterValue(fromInput.value)}-${formatter.formatFilterValue(toInput.value)}`,
                urlKey: params.urlKey,
                remove() {
                    fromInput.value = Number(slider._minValue);
                    toInput.value = Number(slider._maxValue);
                    updateSliderValues();
                    updateResultViewCriteria();
                    notifyFilterAdded();
                },
            });
        } else {
            eventHub.publish('filters:removed', resultViewName);
        }
    }

    function listenForUserChanges() {
        const changeListeners = function () {
            updateResultViewCriteria();
            notifyFilterAdded();
            params.eventHub.publish('resultViewCriteria:changed');
        };
        dom.on(fromInput, 'change', changeListeners);
        dom.on(toInput, 'change', changeListeners);
    }

    function prependMaxLimitLabel(maxValue) {
        if (maxValue) {
            dom.data.set({ 'unbounded-output-end': '' }, toInput);
            dom.data.set({ 'unbounded-output': 'over {value}' }, toInput);
        }
    }

    function setupSlider(values) {
        hasMaxLimit = values.maxLimit > 0;
        prependMaxLimitLabel(values.maxLimit);

        slider = createSlider({
            root: params.root,
            from: fromInput,
            to: toInput,
            minValue: Number(formatter.calculateSliderValue(values.min)),
            maxValue: Number(formatter.calculateSliderValue(values.maxLimit || values.max)),
            opts: {
                startFormatter: formatter.formatStartLabel,
                endFormatter: formatter.formatEndLabel,
            },
        });
    }

    eventHub.subscribe('first-stats', (stats) => {
        const sliderStats = stats[resultViewName];
        if (sliderStats) {
            setupSlider(sliderStats);
            if (params.urlQueryParams[params.urlKey]) {
                updateSliderValues(params.urlQueryParams[params.urlKey]);
                notifyFilterAdded();
            } else {
                updateSliderValues();
            }
            listenForUserChanges();
            updateResultViewCriteria();
        }
    });

    eventHub.subscribe('urlQueryParams:changed', () => {
        updateSliderValues(params.urlQueryParams[params.urlKey]);
        updateResultViewCriteria();
        notifyFilterAdded();
    });
}
